import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import CommissionRoi from './CommissionRoi';

function fmtMoney(n) { return `$${Number(n || 0).toFixed(2)}`; }

function typeBadge(saleType) {
  if (saleType?.startsWith('new'))     return <span className="badge badge-blue">New</span>;
  if (saleType?.startsWith('winback')) return <span className="badge badge-orange">Winback</span>;
  if (saleType?.startsWith('save'))    return <span className="badge badge-gray">Save</span>;
  return <span className="badge badge-gray">—</span>;
}

// ─── Void modal ─────────────────────────────────────────────────────────────

function VoidModal({ sale, onClose, onVoided }) {
  const { post } = useApi();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleVoid() {
    if (!reason.trim()) { setError('A reason is required to void a sale.'); return; }
    setSaving(true); setError(null);
    try {
      await post(`/commission/sales/${sale.sale_id}/void`, { reason: reason.trim() });
      onVoided();
    } catch (e) {
      setError(e.message || 'Failed to void sale.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-title">Void Sale — {sale.member_first_name} {sale.member_last_name}</div>
        {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{error}</div>}
        <div className="field-group">
          <label className="field-label">Reason (required)</label>
          <textarea className="field-textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={saving} onClick={handleVoid}>{saving ? 'Voiding...' : 'Void Sale'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Rate settings modal ────────────────────────────────────────────────────

function RateSettingsModal({ repUserId, repName, onClose }) {
  const { get, put } = useApi();
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get(`/commission/settings/${repUserId}`).then(setRates).catch(console.error);
  }, [repUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(f, v) { setRates(r => ({ ...r, [f]: v })); }

  async function handleSave() {
    setSaving(true);
    try {
      await put(`/commission/settings/${repUserId}`, {
        rateNewMonthly: Number(rates.rate_new_monthly),
        rateNewAnnual: Number(rates.rate_new_annual),
        rateWinbackMonthly: Number(rates.rate_winback_monthly),
        rateWinbackAnnual: Number(rates.rate_winback_annual),
        rateSaveMonthly: Number(rates.rate_save_monthly),
        rateSaveAnnual: Number(rates.rate_save_annual),
        baseSalary: rates.base_salary === '' || rates.base_salary == null ? null : Number(rates.base_salary),
        employerTaxRate: Number(rates.employer_tax_rate ?? 0.0765),
        workersCompRate: Number(rates.workers_comp_rate ?? 0.0100),
        employmentStartDate: rates.employment_start_date || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  if (!rates) return null;

  const FIELDS = [
    ['rate_new_monthly', 'New — Monthly'], ['rate_new_annual', 'New — Annual'],
    ['rate_winback_monthly', 'Winback — Monthly'], ['rate_winback_annual', 'Winback — Annual'],
    ['rate_save_monthly', 'Save — Monthly'], ['rate_save_annual', 'Save — Annual'],
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-title">Commission Rates — {repName || repUserId}</div>
        {rates.is_default && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
            No custom rates set yet — showing standard defaults (22%/14%/5%).
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
          {FIELDS.map(([key, label]) => (
            <div className="field-group" key={key}>
              <label className="field-label">{label}</label>
              <input type="number" step="0.0001" className="field-input"
                value={rates[key]} onChange={e => update(key, e.target.value)} />
            </div>
          ))}
        </div>

        <div style={{ marginTop: '0.9rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Payroll (feeds the payroll-based payback multiple on the ROI tab)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
            <div className="field-group">
              <label className="field-label">Base Salary (annual)</label>
              <input type="number" step="100" className="field-input"
                value={rates.base_salary ?? ''} onChange={e => update('base_salary', e.target.value)} placeholder="e.g. 45000" />
            </div>
            <div className="field-group">
              <label className="field-label">Employment Start Date</label>
              <input type="date" className="field-input"
                value={rates.employment_start_date ? String(rates.employment_start_date).slice(0, 10) : ''}
                onChange={e => update('employment_start_date', e.target.value)} />
            </div>
            <div className="field-group">
              <label className="field-label">Employer Tax Rate (SS + Medicare)</label>
              <input type="number" step="0.0001" className="field-input"
                value={rates.employer_tax_rate ?? 0.0765} onChange={e => update('employer_tax_rate', e.target.value)} />
            </div>
            <div className="field-group">
              <label className="field-label">Workers' Comp Rate</label>
              <input type="number" step="0.0001" className="field-input"
                value={rates.workers_comp_rate ?? 0.0100} onChange={e => update('workers_comp_rate', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Rates'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export default function CommissionAdmin() {
  const { get } = useApi();
  const [tab, setTab] = useState('sales');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [overview, setOverview] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voidTarget, setVoidTarget] = useState(null);
  const [rateTarget, setRateTarget] = useState(null);
  const [filters, setFilters] = useState({ rep: '', club: '', saleType: '', status: '' });

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ month, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
    Promise.all([
      get(`/commission/overview?month=${month}`),
      get(`/commission/sales?${params.toString()}`),
    ])
      .then(([ov, sl]) => { setOverview(ov); setSales(sl.sales || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // The export endpoint requires the same Bearer token as every other API
  // call, so a plain <a href> won't work (no way to attach the header to a
  // browser-navigated download). Fetch the JSON via the authenticated get(),
  // build real CSV client-side, then trigger the download via a Blob —
  // matches spec Sec 5.4's "matches the format of the current manual CSV".
  const [exporting, setExporting] = useState(false);
  async function handleExport() {
    setExporting(true);
    try {
      const rows = await get(`/commission/export?month=${month}`);
      const headers = ['sale_date', 'rep_name', 'member_first_name', 'member_last_name',
        'member_email', 'location_id', 'membership_plan', 'sale_price', 'billing_cycle',
        'sale_type', 'commission_rate', 'commission_amount', 'status', 'notes'];
      const csvEscape = v => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [headers.join(',')]
        .concat(rows.map(r => headers.map(h => csvEscape(r[h])).join(',')))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      {voidTarget && (
        <VoidModal sale={voidTarget} onClose={() => setVoidTarget(null)} onVoided={() => { setVoidTarget(null); load(); }} />
      )}
      {rateTarget && (
        <RateSettingsModal repUserId={rateTarget.rep_user_id} repName={rateTarget.rep_name} onClose={() => setRateTarget(null)} />
      )}

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        <button className={`btn btn-sm ${tab === 'sales' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('sales')}>Sales</button>
        <button className={`btn btn-sm ${tab === 'roi' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('roi')}>ROI</button>
      </div>

      {tab === 'roi' ? <CommissionRoi /> : <>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input type="month" className="field-input" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 150 }} />
        <select className="field-select" value={filters.saleType} onChange={e => setFilters(f => ({ ...f, saleType: e.target.value }))}>
          <option value="">All Types</option>
          <option value="new_monthly">New — Monthly</option>
          <option value="new_annual">New — Annual</option>
          <option value="winback_monthly">Winback — Monthly</option>
          <option value="winback_annual">Winback — Annual</option>
          <option value="save_monthly">Save — Monthly</option>
          <option value="save_annual">Save — Annual</option>
        </select>
        <select className="field-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="duplicate_flagged">Duplicate Flagged</option>
          <option value="voided">Voided</option>
        </select>
        <button className="btn btn-ghost btn-sm" disabled={exporting} onClick={handleExport}>
          {exporting ? 'Exporting...' : '⬇ Export CSV'}
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : (
        <>
          {overview && (
            <div style={styles.kpiBar}>
              <div style={styles.kpiItem}><div style={styles.kpiValue}>{overview.total_sales}</div><div style={styles.kpiLabel}>Total Sales</div></div>
              <div style={styles.kpiItem}><div style={{ ...styles.kpiValue, color: 'var(--green)' }}>{fmtMoney(overview.commission_earned)}</div><div style={styles.kpiLabel}>Commissions Owed</div></div>
              <div style={styles.kpiItem}><div style={styles.kpiValue}>{overview.new_count}</div><div style={styles.kpiLabel}>New</div></div>
              <div style={styles.kpiItem}><div style={{ ...styles.kpiValue, color: 'var(--orange)' }}>{overview.winback_count}</div><div style={styles.kpiLabel}>Winback</div></div>
              <div style={styles.kpiItem}><div style={styles.kpiValue}>{overview.save_count}</div><div style={styles.kpiLabel}>Save</div></div>
              {overview.duplicate_flagged_count > 0 && (
                <div style={{ ...styles.kpiItem, background: 'rgba(239,68,68,0.08)' }}>
                  <div style={{ ...styles.kpiValue, color: 'var(--red)' }}>{overview.duplicate_flagged_count}</div>
                  <div style={styles.kpiLabel}>Flagged Dupes</div>
                </div>
              )}
            </div>
          )}

          {overview?.byRep?.length > 0 && (
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {overview.byRep.map(r => (
                <div key={r.rep_user_id} style={{ ...styles.repCard }}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{r.rep_name || r.rep_user_id}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.total_sales} sales · {fmtMoney(r.commission_earned)}</div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setRateTarget(r)}>Edit Rates</button>
                </div>
              ))}
            </div>
          )}

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Rep</th>
                  <th style={styles.th}>Member</th>
                  <th style={styles.th}>Club</th>
                  <th style={styles.th}>Plan</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Price</th>
                  <th style={styles.th}>Commission</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {sales.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No sales for this period</td></tr>
                )}
                {sales.map(s => (
                  <tr key={s.sale_id} className="table-row-hover">
                    <td style={styles.td}>{s.sale_date}</td>
                    <td style={styles.td}>{s.rep_name || s.rep_user_id}</td>
                    <td style={styles.td}>{s.member_first_name} {s.member_last_name}</td>
                    <td style={styles.td}><span className="badge badge-gray">{s.location_id}</span></td>
                    <td style={styles.td}>{s.membership_plan}</td>
                    <td style={styles.td}>{typeBadge(s.sale_type)}</td>
                    <td style={styles.td}>{fmtMoney(s.sale_price)}</td>
                    <td style={{ ...styles.td, fontWeight: 700, color: 'var(--green)' }}>{fmtMoney(s.commission_amount)}</td>
                    <td style={styles.td}>
                      {s.status === 'active' && <span className="badge badge-green">Active</span>}
                      {s.status === 'duplicate_flagged' && <span className="badge badge-red">Dupe Flagged</span>}
                      {s.status === 'voided' && <span className="badge badge-gray">Voided</span>}
                    </td>
                    <td style={styles.td}>
                      {s.status !== 'voided' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setVoidTarget(s)}>Void</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>}
    </div>
  );
}

const styles = {
  kpiBar: {
    display: 'flex', gap: 0, background: '#141414', border: '1px solid var(--border)',
    borderRadius: 8, overflow: 'hidden', marginBottom: '1rem', flexWrap: 'wrap',
  },
  kpiItem: { flex: 1, padding: '0.6rem 0.75rem', borderRight: '1px solid var(--border)', minWidth: 100, textAlign: 'center' },
  kpiValue: { fontSize: '1.15rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, fontFamily: 'DM Mono, monospace' },
  kpiLabel: { fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '0.15rem', whiteSpace: 'nowrap' },
  repCard: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.85rem', minWidth: 160 },
  tableWrapper: { overflowX: 'auto', background: '#141414', border: '1px solid var(--border)', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem', minWidth: 900 },
  th: {
    padding: '0.55rem 0.85rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid var(--border)', background: '#141414', whiteSpace: 'nowrap',
  },
  td: { padding: '0.55rem 0.85rem', borderBottom: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'nowrap' },
};
